import { sectionPermissions } from '@qronstat/shared';
import type { FilterNode } from '@qronstat/shared';
import type { ScreenDef } from './types';

const installed: FilterNode = { member: 'stations.is_installed', operator: 'isTrue' };
const stateInstalled: FilterNode = { member: 'station_state.is_installed', operator: 'isTrue' };
const common = ['period', 'city', 'group', 'location', 'station', 'tariff', 'realOnly', 'timeMode'] as const;

export const screens: ScreenDef[] = [
  {
    id: 'summary',
    title: 'nav.summary',
    permission: sectionPermissions.summary,
    quickFilters: ['city', 'group', 'realOnly'],
    defaultPeriod: { preset: 'today' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.summary.kpis',
        span: 12,
        items: [
          {
            metric: 'rents.count',
            breakdown: ['rents.paid_count', 'rents.promo_count', 'rents.open_count'],
            compare: ['same_time_yesterday', 'week_ago'],
            spark: true,
            usual: true,
            link: '/rentals/overview',
          },
          {
            metric: 'money.net_revenue',
            notes: ['rents.avg_check'],
            compare: ['same_time_yesterday', 'week_ago'],
            spark: true,
            usual: true,
            link: '/rentals/revenue',
          },
          {
            metric: 'station_state.online_count',
            ofMetric: 'station_state.installed_count',
            notes: [
              'station_state.online_share',
              'station_state.offline_count',
              'station_state.doubtful_count',
            ],
            sparkMetric: 'station_hours.avg_online',
            link: '/stations/online',
          },
          {
            metric: 'registrations.count',
            notes: ['derived.sber_share'],
            compare: ['same_time_yesterday', 'week_ago'],
            spark: true,
            usual: true,
            link: '/clients/registrations',
          },
          {
            metric: 'rents.debt_amount_total',
            notes: ['rents.debtors', 'rents.avg_debt_age', 'rents.unpaid_count'],
            period: { preset: 'all' },
            link: '/rentals/debts',
          },
        ],
      },
      {
        id: 'trend',
        kind: 'timeseries',
        title: 'screen.summary.trend',
        span: 8,
        metrics: ['rents.count', 'money.net_revenue'],
        chart: 'barline',
        granularities: ['hour', 'day', 'week', 'month'],
        defaultGranularity: 'day',
        autoGranularity: true,
        compare: 'previous',
      },
      {
        id: 'cities',
        kind: 'ranking',
        title: 'screen.summary.cities',
        span: 4,
        objectType: 'city',
        metrics: ['money.net_revenue', 'rents.count'],
        limit: 8,
      },
      {
        id: 'venues',
        kind: 'ranking',
        title: 'screen.summary.venues',
        span: 6,
        objectType: 'location',
        metrics: ['money.net_revenue', 'rents.count'],
        limit: 10,
      },
      {
        id: 'offline',
        kind: 'objects',
        title: 'screen.summary.offline',
        span: 6,
        query: {
          objectType: 'station',
          attributes: [
            'locations.name',
            'cities.name',
            'station_state.offline_minutes',
            'station_state.is_open_now',
          ],
          metrics: [],
          filters: [stateInstalled, { member: 'station_state.is_online', operator: 'isFalse' }],
          order: [{ member: 'station_state.offline_minutes', direction: 'desc' }],
          limit: 6,
          offset: 0,
        },
        moreHref: '/stations/online',
      },
      { id: 'funnel', kind: 'funnel', title: 'screen.summary.funnel', span: 6 },
      {
        id: 'counterparties',
        kind: 'ranking',
        title: 'screen.summary.counterparties',
        span: 6,
        objectType: 'counterparty',
        metrics: ['payments.payments_count', 'payments.turnover'],
        limit: 6,
      },
      { id: 'map', kind: 'map', title: 'screen.summary.map', span: 8, compact: true },
      { id: 'alerts', kind: 'alerts', title: 'screen.summary.alerts', span: 4, limit: 6 },
    ],
  },
  {
    id: 'rentals.overview',
    title: 'nav.rentals.overview',
    permission: sectionPermissions.rentals,
    quickFilters: [...common],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          {
            metric: 'rents.count',
            breakdown: ['rents.paid_count', 'rents.promo_count'],
            compare: ['previous'],
            spark: true,
          },
          { metric: 'rents.paid_count', compare: ['previous'] },
          { metric: 'rents.promo_share', compare: ['previous'] },
          { metric: 'rents.clients', compare: ['previous'] },
          { metric: 'rents.open_count', period: { preset: 'all' }, sparkMetric: 'rents.count' },
          {
            metric: 'open_rents.expected_next_day_amount',
            notes: ['open_rents.count'],
            sparkMetric: 'money.net_revenue',
          },
          {
            metric: 'open_rents.purchase_soon_count',
            notes: ['open_rents.purchase_soon_amount', 'open_rents.overdue_purchase_count'],
            meter: { of: 'open_rents.count' },
          },
        ],
      },
      {
        id: 'trend',
        kind: 'timeseries',
        title: 'screen.rentals.trend',
        span: 12,
        metrics: ['rents.count', 'rents.paid_count', 'rents.promo_count'],
        chart: 'bar',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'day',
        stacked: false,
      },
      { id: 'heatmap', kind: 'heatmap', title: 'screen.rentals.heatmap', span: 8, metric: 'rents.count' },
      {
        id: 'cities',
        kind: 'distribution',
        title: 'screen.rentals.byCity',
        span: 4,
        dimension: 'cities.name',
        metric: 'rents.count',
        chart: 'bar',
      },
      { id: 'records', kind: 'records', title: 'screen.rentals.records', span: 12, metric: 'rents.count' },
      {
        id: 'compareCities',
        kind: 'compare',
        title: 'screen.rentals.compareCities',
        span: 12,
        objectType: 'city',
        metric: 'rents.count',
        granularity: 'week',
      },
    ],
  },
  {
    id: 'rentals.revenue',
    title: 'nav.rentals.revenue',
    permission: sectionPermissions.rentals,
    quickFilters: [...common],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          { metric: 'money.revenue', compare: ['previous'], spark: true },
          { metric: 'money.refunds', compare: ['previous'] },
          { metric: 'money.net_revenue', compare: ['previous'], spark: true },
          { metric: 'rents.avg_check', compare: ['previous'] },
          { metric: 'derived.revenue_per_station', compare: ['previous'] },
          { metric: 'derived.revenue_per_slot', compare: ['previous'] },
          { metric: 'money.venue_share', compare: ['previous'] },
          { metric: 'derived.revenue_per_client', compare: ['previous'] },
        ],
      },
      {
        id: 'trend',
        kind: 'timeseries',
        title: 'screen.revenue.trend',
        span: 12,
        metrics: ['money.revenue', 'money.refunds', 'money.net_revenue'],
        chart: 'line',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'day',
      },
      {
        id: 'cities',
        kind: 'ranking',
        title: 'screen.revenue.cities',
        span: 6,
        objectType: 'city',
        metrics: ['money.net_revenue', 'derived.revenue_per_station'],
        limit: 10,
      },
      {
        id: 'venues',
        kind: 'ranking',
        title: 'screen.revenue.venues',
        span: 6,
        objectType: 'location',
        metrics: ['money.net_revenue', 'money.venue_share'],
        limit: 10,
      },
      {
        id: 'stations',
        kind: 'ranking',
        title: 'screen.revenue.stations',
        span: 6,
        objectType: 'station',
        metrics: ['money.net_revenue', 'rents.count'],
        limit: 10,
      },
      {
        id: 'tariffs',
        kind: 'ranking',
        title: 'screen.revenue.tariffs',
        span: 6,
        objectType: 'tariff',
        metrics: ['money.net_revenue', 'rents.count'],
        limit: 10,
      },
      {
        id: 'weekdays',
        kind: 'heatmap',
        title: 'screen.revenue.weekdays',
        span: 12,
        metric: 'money.net_revenue',
      },
    ],
  },
  {
    id: 'rentals.debts',
    title: 'nav.rentals.debts',
    permission: sectionPermissions.rentals,
    quickFilters: [...common],
    defaultPeriod: { preset: 'all' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          { metric: 'rents.debt_amount_total', period: { preset: 'all' } },
          { metric: 'rents.debtors', period: { preset: 'all' } },
          { metric: 'rents.avg_debt_age', period: { preset: 'all' } },
          { metric: 'rent_charges.debt_repaid_amount', compare: ['previous'], period: { preset: 'last30' } },
          {
            metric: 'rents.unpaid_purchase_count',
            notes: ['rents.unpaid_purchase_amount'],
            period: { preset: 'all' },
          },
          {
            metric: 'rents.debt_after_free_start_count',
            notes: ['rents.debt_after_free_start_amount'],
            period: { preset: 'all' },
          },
        ],
      },
      {
        id: 'age',
        kind: 'distribution',
        title: 'screen.debts.age',
        span: 6,
        dimension: 'rents.debt_age_bucket',
        metric: 'rents.debt_amount_total',
        chart: 'column',
        period: { preset: 'all' },
      },
      {
        id: 'repaid',
        kind: 'timeseries',
        title: 'screen.debts.repaid',
        span: 6,
        metrics: ['rent_charges.debt_repaid_amount', 'rent_charges.debt_repaid_count'],
        chart: 'bar',
        granularities: ['month', 'week'],
        defaultGranularity: 'month',
        period: { preset: 'all' },
      },
      {
        id: 'records',
        kind: 'records',
        title: 'screen.debts.records',
        span: 12,
        metric: 'rents.unpaid_count',
      },
    ],
  },
  {
    id: 'rentals.promo',
    title: 'nav.rentals.promo',
    permission: sectionPermissions.rentals,
    quickFilters: [...common],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          { metric: 'rents.promo_count', compare: ['previous'], spark: true },
          { metric: 'rents.promo_share', compare: ['previous'] },
          { metric: 'rents.paid_count', compare: ['previous'] },
          { metric: 'rents.promo_cost_amount', compare: ['previous'] },
        ],
      },
      {
        id: 'share',
        kind: 'timeseries',
        title: 'screen.promo.share',
        span: 8,
        metrics: ['rents.promo_share'],
        chart: 'line',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'day',
      },
      {
        id: 'kind',
        kind: 'distribution',
        title: 'screen.promo.kinds',
        span: 4,
        dimension: 'rents.promo_kind',
        metric: 'rents.promo_count',
        chart: 'donut',
      },
      {
        id: 'cities',
        kind: 'ranking',
        title: 'screen.promo.cities',
        span: 12,
        objectType: 'city',
        metrics: ['rents.promo_share', 'rents.promo_count', 'rents.count'],
        limit: 15,
      },
    ],
  },
  {
    id: 'rentals.duration',
    title: 'nav.rentals.duration',
    permission: sectionPermissions.rentals,
    quickFilters: [...common],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          { metric: 'rents.avg_duration', compare: ['previous'] },
          { metric: 'rents.median_duration', compare: ['previous'] },
          { metric: 'rents.short_count', compare: ['previous'] },
          { metric: 'rents.short_share', compare: ['previous'] },
          { metric: 'rents.returned_elsewhere_count', compare: ['previous'] },
          { metric: 'rents.purchase_count', compare: ['previous'] },
        ],
      },
      {
        id: 'thresholds',
        kind: 'thresholds',
        title: 'screen.duration.thresholds',
        span: 12,
        metric: 'rents.count',
        member: 'rents.duration_minutes',
        bands: [
          { key: 'over30m', from: 30 },
          { key: 'over1h', from: 60 },
          { key: 'h1_24', from: 60, to: 1440 },
          { key: 'over1d', from: 1440 },
        ],
        custom: true,
      },
      {
        id: 'buckets',
        kind: 'distribution',
        title: 'screen.duration.buckets',
        span: 12,
        dimension: 'rents.duration_bucket',
        metric: 'rents.count',
        chart: 'column',
      },
      {
        id: 'tariffs',
        kind: 'ranking',
        title: 'screen.duration.tariffs',
        span: 6,
        objectType: 'tariff',
        metrics: ['rents.median_duration', 'rents.avg_duration', 'rents.count'],
        limit: 10,
      },
      {
        id: 'cities',
        kind: 'ranking',
        title: 'screen.duration.cities',
        span: 6,
        objectType: 'city',
        metrics: ['rents.median_duration', 'rents.avg_duration', 'rents.count'],
        limit: 10,
      },
    ],
  },
  {
    id: 'rentals.tariffs',
    title: 'nav.rentals.tariffs',
    permission: sectionPermissions.rentals,
    quickFilters: ['period', 'city', 'group', 'location', 'realOnly'],
    defaultPeriod: { preset: 'last30' },
    widgets: [{ id: 'tariffs', kind: 'tariffs', title: 'screen.tariffs.list', span: 12 }],
  },
  {
    id: 'stations.overview',
    title: 'nav.stations.overview',
    permission: sectionPermissions.stations,
    quickFilters: ['period', 'city', 'group', 'location', 'realOnly', 'timeMode'],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          {
            metric: 'station_state.installed_count',
            notes: ['station_state.slots_in_stations', 'station_state.power_banks_in_stations'],
            sparkMetric: 'station_days.avg_installed',
          },
          {
            metric: 'station_state.online_count',
            ofMetric: 'station_state.installed_count',
            notes: ['station_state.offline_count', 'station_state.doubtful_count'],
            sparkMetric: 'station_hours.avg_online',
          },
          {
            metric: 'station_state.online_share',
            notes: ['station_state.offline_by_schedule_count'],
            sparkMetric: 'station_hours.online_time_share',
          },
          { metric: 'station_hours.online_time_share', compare: ['previous'], link: '/stations/online' },
          {
            metric: 'station_hours.issue_ready_share',
            notes: ['station_hours.no_charged_online_hours', 'station_hours.stations_without_charged'],
            compare: ['previous'],
          },
          { metric: 'station_hours.avg_online', compare: ['previous'], link: '/stations/online' },
          { metric: 'rents.stations_with_rents', compare: ['previous'] },
        ],
      },
      {
        id: 'online',
        kind: 'timeseries',
        title: 'screen.stations.onlineTrend',
        span: 12,
        metrics: ['station_hours.avg_online', 'station_days.avg_installed'],
        chart: 'line',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'day',
      },
      {
        id: 'availability',
        kind: 'timeseries',
        title: 'screen.stations.availability',
        span: 12,
        metrics: ['station_hours.online_time_share', 'station_hours.issue_ready_share'],
        chart: 'line',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'day',
        toggle: {
          label: 'availability.openHours',
          on: [{ member: 'station_hours.is_open_hour', operator: 'isTrue' }],
          off: [{ member: 'station_hours.is_open_hour', operator: 'isFalse' }],
        },
      },
      {
        id: 'cities',
        kind: 'ranking',
        title: 'screen.stations.cities',
        span: 6,
        objectType: 'city',
        metrics: ['station_state.online_share', 'station_state.installed_count'],
        limit: 12,
      },
      {
        id: 'slots',
        kind: 'distribution',
        title: 'screen.stations.slots',
        span: 3,
        dimension: 'stations.slots',
        metric: 'stations.installed_count',
        chart: 'donut',
      },
      {
        id: 'link',
        kind: 'distribution',
        title: 'screen.stations.connection',
        span: 3,
        dimension: 'stations.connection_type',
        metric: 'stations.installed_count',
        chart: 'donut',
      },
      {
        id: 'compare',
        kind: 'compare',
        title: 'screen.stations.compare',
        span: 12,
        objectType: 'station',
        metric: 'rents.count',
        granularity: 'day',
      },
    ],
  },
  {
    id: 'stations.online',
    title: 'nav.stations.online',
    permission: sectionPermissions.stations,
    quickFilters: ['city', 'group', 'location', 'realOnly'],
    defaultPeriod: { preset: 'today' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          {
            metric: 'station_state.online_count',
            ofMetric: 'station_state.installed_count',
            notes: ['station_state.online_share'],
            sparkMetric: 'station_hours.avg_online',
          },
          {
            metric: 'station_state.offline_count',
            notes: ['station_state.max_offline_minutes'],
            meter: { of: 'station_state.installed_count' },
          },
          {
            metric: 'station_state.doubtful_count',
            meter: { of: 'station_state.installed_count' },
          },
          {
            metric: 'station_state.offline_by_schedule_count',
            meter: { of: 'station_state.installed_count' },
          },
        ],
      },
      {
        id: 'list',
        kind: 'objects',
        title: 'screen.online.list',
        span: 12,
        offlineFilter: true,
        query: {
          objectType: 'station',
          attributes: [
            'locations.name',
            'cities.name',
            'station_state.link_state',
            'station_state.offline_minutes',
            'station_state.last_heartbeat',
            'station_state.is_open_now',
            'station_state.power_banks',
            'stations.connection_type',
          ],
          metrics: [],
          filters: [stateInstalled],
          order: [{ member: 'station_state.offline_minutes', direction: 'desc' }],
          limit: 500,
          offset: 0,
        },
      },
    ],
  },
  {
    id: 'rentals.refusals',
    title: 'nav.rentals.refusals',
    permission: sectionPermissions.rentals,
    quickFilters: ['period', 'city', 'group', 'location', 'realOnly', 'timeMode'],
    defaultPeriod: { preset: 'last7' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          {
            metric: 'station_events.eject_failures',
            notes: ['station_events.eject_failure_share', 'station_events.stations_with_eject_failures'],
            compare: ['previous'],
          },
          {
            metric: 'reconciliation.paid_without_rent_count',
            notes: ['reconciliation.paid_without_rent_amount', 'reconciliation.refunded_without_rent_amount'],
            compare: ['previous'],
          },
          {
            metric: 'shop_orders.rental_abandoned_count',
            compare: ['previous'],
          },
          {
            metric: 'rent_charges.declined_count',
            notes: ['rent_charges.declined_share'],
            compare: ['previous'],
          },
        ],
      },
      {
        id: 'trend',
        kind: 'timeseries',
        title: 'screen.refusals.trend',
        span: 12,
        metrics: [
          'station_events.eject_failures',
          'reconciliation.paid_without_rent_count',
          'shop_orders.rental_abandoned_count',
        ],
        chart: 'bar',
        granularities: ['hour', 'day', 'week', 'month'],
        defaultGranularity: 'day',
        autoGranularity: true,
      },
      {
        id: 'reasons',
        kind: 'distribution',
        title: 'screen.health.reasons',
        span: 4,
        dimension: 'station_events.code',
        metric: 'station_events.eject_failures',
        chart: 'donut',
      },
      {
        id: 'stations',
        kind: 'ranking',
        title: 'screen.refusals.stations',
        span: 8,
        objectType: 'station',
        metrics: ['station_events.eject_failures', 'reconciliation.paid_without_rent_count'],
        limit: 15,
      },
      {
        id: 'locations',
        kind: 'ranking',
        title: 'screen.refusals.locations',
        span: 12,
        objectType: 'location',
        metrics: [
          'station_events.eject_failures',
          'rent_charges.declined_count',
          'reconciliation.paid_without_rent_count',
          'station_hours.offline_hours',
          'station_hours.no_charged_online_hours',
        ],
        limit: 15,
      },
      {
        id: 'withoutRent',
        kind: 'records',
        title: 'screen.refusals.withoutRent',
        span: 12,
        metric: 'reconciliation.paid_without_rent_count',
      },
    ],
  },
  {
    id: 'payments.reconciliation',
    title: 'nav.payments.reconciliation',
    permission: sectionPermissions.payments,
    quickFilters: ['period'],
    defaultPeriod: { preset: 'prevMonth' },
    widgets: [{ id: 'reconciliation', kind: 'reconciliation', title: 'settings.reconciliation', span: 12 }],
  },
  {
    id: 'stations.health',
    title: 'nav.stations.health',
    permission: sectionPermissions.stations,
    quickFilters: ['period', 'city', 'group', 'location', 'realOnly', 'timeMode'],
    defaultPeriod: { preset: 'last7' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          {
            metric: 'station_events.joins_count',
            notes: ['station_events.stations_joined'],
            compare: ['previous'],
          },
          {
            metric: 'station_events.eject_failure_share',
            notes: ['station_events.eject_failures', 'station_events.ejects_count'],
            compare: ['previous'],
          },
          {
            metric: 'station_events.stations_weak_signal',
            notes: ['station_events.avg_csq'],
          },
          {
            metric: 'slot_hours.stalled_slots',
            notes: ['slot_hours.stalled_share'],
          },
          {
            metric: 'slot_hours.unreadable_slots',
            notes: ['slot_hours.error_slots'],
          },
          {
            metric: 'slot_hours.max_temperature',
            notes: ['slot_hours.avg_temperature'],
          },
        ],
      },
      {
        id: 'trend',
        kind: 'timeseries',
        title: 'screen.health.trend',
        span: 12,
        metrics: ['station_events.joins_count', 'station_events.eject_failures'],
        chart: 'bar',
        granularities: ['hour', 'day', 'week'],
        defaultGranularity: 'day',
        autoGranularity: true,
      },
      {
        id: 'unstable',
        kind: 'ranking',
        title: 'screen.health.unstable',
        span: 6,
        objectType: 'station',
        metrics: ['station_events.joins_count', 'station_events.eject_failures'],
        limit: 15,
      },
      {
        id: 'failures',
        kind: 'ranking',
        title: 'screen.health.failures',
        span: 6,
        objectType: 'station',
        metrics: ['station_events.eject_failures', 'station_events.eject_failure_share'],
        limit: 15,
      },
      {
        id: 'reasons',
        kind: 'distribution',
        title: 'screen.health.reasons',
        span: 4,
        dimension: 'station_events.code',
        metric: 'station_events.eject_failures',
        chart: 'donut',
      },
      {
        id: 'signal',
        kind: 'distribution',
        title: 'screen.health.signal',
        span: 4,
        dimension: 'station_events.signal_level',
        metric: 'station_events.stations_joined',
        chart: 'donut',
      },
      {
        id: 'firmware',
        kind: 'distribution',
        title: 'screen.health.firmware',
        span: 4,
        dimension: 'station_events.software_version',
        metric: 'station_events.stations_joined',
        chart: 'bar',
      },
      {
        id: 'slots',
        kind: 'ranking',
        title: 'screen.health.slots',
        span: 12,
        objectType: 'station',
        metrics: [
          'slot_hours.stalled_slots',
          'slot_hours.unreadable_slots',
          'slot_hours.error_slots',
          'slot_hours.max_temperature',
        ],
        limit: 20,
      },
    ],
  },
  {
    id: 'coverage',
    title: 'nav.coverage',
    permission: sectionPermissions.stations,
    quickFilters: ['period', 'city', 'group', 'location', 'tariff', 'realOnly'],
    defaultPeriod: { preset: 'today' },
    widgets: [{ id: 'map', kind: 'map', title: 'nav.coverage', span: 12 }],
  },
  {
    id: 'stations.new',
    title: 'nav.stations.new',
    permission: sectionPermissions.stations,
    quickFilters: ['period', 'city', 'group', 'realOnly'],
    defaultPeriod: { preset: 'last90' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          { metric: 'stations.new_count', compare: ['previous'] },
          { metric: 'stations.stable_new_count', compare: ['previous'] },
        ],
      },
      {
        id: 'trend',
        kind: 'timeseries',
        title: 'screen.new.trend',
        span: 8,
        metrics: ['stations.new_count'],
        chart: 'bar',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'week',
        timeMember: 'stations.installed_at',
      },
      {
        id: 'cities',
        kind: 'distribution',
        title: 'screen.new.cities',
        span: 4,
        dimension: 'cities.name',
        metric: 'stations.new_count',
        chart: 'bar',
      },
      {
        id: 'list',
        kind: 'objects',
        title: 'screen.new.list',
        span: 12,
        usePeriod: true,
        query: {
          objectType: 'station',
          attributes: [
            'locations.name',
            'cities.name',
            'stations.installed_at',
            'stations.installed_estimated',
            'stations.first_stable_date',
            'stations.slots',
          ],
          metrics: ['rents.count', 'money.net_revenue'],
          filters: [installed],
          order: [{ member: 'stations.installed_at', direction: 'desc' }],
          limit: 500,
          offset: 0,
        },
      },
      {
        id: 'sinceStart',
        kind: 'sinceStart',
        title: 'screen.new.sinceStart',
        span: 12,
        objectType: 'station',
        metric: 'rents.count',
        granularity: 'week',
        limit: 8,
        pick: 'newest',
        filters: [installed],
      },
    ],
  },
  {
    id: 'stations.idle',
    title: 'nav.stations.idle',
    permission: sectionPermissions.stations,
    quickFilters: ['period', 'city', 'group', 'location', 'realOnly'],
    defaultPeriod: { preset: 'last7' },
    widgets: [
      {
        id: 'idle',
        kind: 'objects',
        title: 'screen.idle.list',
        span: 12,
        reasons: true,
        usePeriod: true,
        query: {
          objectType: 'station',
          attributes: [
            'cities.name',
            'locations.name',
            'stations.installed_at',
            'stations.last_rent_at',
            'station_state.link_state',
            'station_state.power_banks',
            'station_state.charged',
            'station_state.is_open_now',
            'stations.tariff_title',
          ],
          metrics: ['rents.count'],
          filters: [installed],
          threshold: { metric: 'rents.count', operator: 'lte', value: 0 },
          order: [{ member: 'stations.last_rent_at', direction: 'asc' }],
          limit: 1000,
          offset: 0,
        },
      },
    ],
  },
  {
    id: 'stations.powerbanks',
    title: 'nav.stations.powerbanks',
    permission: sectionPermissions.stations,
    quickFilters: ['period', 'city', 'group', 'location', 'realOnly'],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          {
            metric: 'power_banks.count',
            notes: ['power_banks.in_service_count', 'power_banks.lost_count'],
            meter: { part: 'power_banks.in_service_count' },
          },
          {
            metric: 'power_banks.in_service_count',
            notes: ['power_banks.charged_share'],
            meter: { of: 'power_banks.count' },
          },
          {
            metric: 'power_banks.lost_count',
            meter: { of: 'power_banks.count' },
          },
          {
            metric: 'station_state.fill_now',
            notes: ['station_state.power_banks_in_stations', 'station_state.slots_in_stations'],
            sparkMetric: 'station_hours.avg_fill',
          },
          {
            metric: 'station_state.charged_in_stations',
            meter: { of: 'station_state.power_banks_in_stations' },
          },
          {
            metric: 'station_state.empty_count',
            meter: { of: 'station_state.installed_count' },
          },
          {
            metric: 'station_state.full_count',
            meter: { of: 'station_state.installed_count' },
          },
          { metric: 'derived.powerbank_turnover', compare: ['previous'], sparkMetric: 'rents.count' },
        ],
      },
      {
        id: 'states',
        kind: 'distribution',
        title: 'screen.powerbanks.states',
        span: 4,
        dimension: 'power_banks.location_state',
        metric: 'power_banks.count',
        chart: 'donut',
      },
      {
        id: 'fill',
        kind: 'timeseries',
        title: 'screen.powerbanks.fill',
        span: 8,
        metrics: ['station_hours.avg_fill'],
        chart: 'line',
        granularities: ['day', 'week'],
        defaultGranularity: 'day',
      },
      {
        id: 'stations',
        kind: 'objects',
        title: 'screen.powerbanks.stations',
        span: 12,
        query: {
          objectType: 'station',
          attributes: [
            'locations.name',
            'cities.name',
            'station_state.power_banks',
            'station_state.slots',
            'station_state.fill_share',
            'station_state.charged',
            'station_state.is_empty',
            'station_state.is_full',
          ],
          metrics: [],
          filters: [stateInstalled],
          order: [{ member: 'station_state.fill_share', direction: 'asc' }],
          limit: 1000,
          offset: 0,
        },
      },
    ],
  },
  {
    id: 'stations.batteries',
    title: 'nav.stations.batteries',
    permission: sectionPermissions.stations,
    quickFilters: ['period', 'city', 'group', 'location', 'realOnly'],
    defaultPeriod: { preset: 'last90' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          {
            metric: 'batteries.measured_count',
            notes: ['batteries.collecting_count', 'batteries.count'],
            meter: { of: 'batteries.count' },
            info: false,
          },
          { metric: 'batteries.median_capacity', sparkMetric: 'battery_bands.median_band_capacity' },
          { metric: 'batteries.median_health', meter: { self: true } },
          { metric: 'batteries.median_cycles', sparkMetric: 'battery_bands.bands_count' },
          {
            metric: 'battery_bands.bands_count',
            compare: ['previous'],
            notes: ['battery_bands.banks_measured'],
          },
        ],
      },
      {
        id: 'wear',
        kind: 'batteryWear',
        title: 'screen.batteries.wear',
        span: 12,
        thresholds: [0.9, 0.8, 0.7, 0.6, 0.5],
        defaultThreshold: 0.7,
      },
      {
        id: 'health',
        kind: 'distribution',
        title: 'screen.batteries.health',
        span: 5,
        dimension: 'batteries.health_band',
        metric: 'batteries.count',
        chart: 'column',
      },
      {
        id: 'trend',
        kind: 'timeseries',
        title: 'screen.batteries.trend',
        span: 7,
        metrics: ['battery_bands.median_band_capacity', 'battery_bands.banks_measured'],
        chart: 'line',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'week',
      },
      {
        id: 'records',
        kind: 'records',
        title: 'screen.batteries.records',
        span: 12,
        metric: 'battery_bands.bands_count',
      },
    ],
  },
  {
    id: 'stations.locations',
    title: 'nav.stations.locations',
    permission: sectionPermissions.stations,
    quickFilters: ['period', 'city', 'group', 'realOnly'],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'locations',
        kind: 'objects',
        title: 'screen.locations.list',
        span: 12,
        usePeriod: true,
        query: {
          objectType: 'location',
          attributes: [
            'cities.name',
            'locations.stations_installed',
            'locations.slots_installed',
            'locations.share_rate',
            'locations.first_rent_at',
            'locations.last_rent_at',
            'locations.schedule',
          ],
          metrics: ['rents.count', 'money.net_revenue', 'money.venue_share'],
          filters: [{ member: 'locations.location_type', operator: 'equals', values: ['venue'] }],
          order: [{ member: 'money.net_revenue', direction: 'desc' }],
          limit: 1000,
          offset: 0,
        },
      },
      {
        id: 'sinceStart',
        kind: 'sinceStart',
        title: 'screen.locations.sinceStart',
        span: 12,
        objectType: 'location',
        metric: 'money.net_revenue',
        granularity: 'week',
        limit: 8,
        pick: 'newest',
        filters: [{ member: 'locations.location_type', operator: 'equals', values: ['venue'] }],
      },
      {
        id: 'compare',
        kind: 'compare',
        title: 'screen.locations.compare',
        span: 12,
        objectType: 'location',
        metric: 'money.net_revenue',
        granularity: 'week',
      },
    ],
  },
  {
    id: 'stations.groups',
    title: 'nav.stations.groups',
    permission: sectionPermissions.stations,
    quickFilters: ['period', 'realOnly'],
    defaultPeriod: { preset: 'last30' },
    widgets: [{ id: 'groups', kind: 'groups', title: 'nav.stations.groups', span: 12 }],
  },
  {
    id: 'clients.funnel',
    title: 'nav.clients.funnel',
    permission: sectionPermissions.clients,
    quickFilters: ['period', 'city', 'group', 'realOnly'],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      { id: 'funnel', kind: 'funnel', title: 'screen.funnel.main', span: 12, splitBy: 'journey.channel' },
    ],
  },
  {
    id: 'clients.registrations',
    title: 'nav.clients.registrations',
    permission: sectionPermissions.clients,
    quickFilters: ['period', 'realOnly'],
    defaultPeriod: { preset: 'last7' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          { metric: 'registrations.count', compare: ['previous'], spark: true, usual: true },
          { metric: 'derived.registrations_per_hour', compare: ['previous'] },
          { metric: 'derived.sber_share', compare: ['previous'] },
          { metric: 'derived.registration_to_rent', compare: ['previous'] },
          { metric: 'derived.first_rent_same_day_share', compare: ['previous'] },
          { metric: 'registrations.median_hours_to_first_rent', compare: ['previous'] },
          {
            metric: 'registrations.rents_total',
            compare: ['previous'],
            notes: ['registrations.with_rent_count'],
          },
        ],
      },
      {
        id: 'conversion',
        kind: 'conversion',
        title: 'screen.registrations.conversion',
        span: 12,
        base: 'registrations.count',
        extra: ['registrations.sber_count', 'derived.sber_share'],
        delay: {
          hours: 'registrations.hours_to_first_rent',
          sameDay: 'registrations.rent_same_day',
          any: 'registrations.has_rent',
        },
        delayWithInternal: {
          hours: 'registrations.hours_to_first_any_rent',
          sameDay: 'registrations.any_rent_same_day',
          any: 'registrations.has_any_rent',
        },
        windows: ['same_day', 1, 3, 7, 14, 30, 'any'],
        defaultWindow: 7,
        granularities: ['day', 'week', 'month'],
        convertedTitle: 'conversion.converted',
      },
      {
        id: 'hourly',
        kind: 'timeseries',
        title: 'screen.registrations.trend',
        span: 12,
        metrics: ['registrations.count'],
        chart: 'bar',
        granularities: ['hour', 'day', 'week', 'month'],
        defaultGranularity: 'day',
        autoGranularity: true,
        usual: true,
      },
      {
        id: 'heatmap',
        kind: 'heatmap',
        title: 'screen.registrations.heatmap',
        span: 12,
        metric: 'registrations.count',
      },
    ],
  },
  {
    id: 'clients.retention',
    title: 'nav.clients.retention',
    permission: sectionPermissions.clients,
    quickFilters: ['period', 'city', 'realOnly'],
    defaultPeriod: { preset: 'all' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          {
            metric: 'clients.with_rent_count',
            period: { preset: 'all' },
            sparkMetric: 'registrations.with_rent_count',
          },
          {
            metric: 'clients.repeat_count',
            period: { preset: 'all' },
            meter: { of: 'clients.with_rent_count' },
          },
          { metric: 'clients.repeat_share', period: { preset: 'all' }, meter: { self: true } },
          { metric: 'rents.repeat_clients', compare: ['previous'], period: { preset: 'last30' } },
          { metric: 'user_events.active_clients', compare: ['previous'], period: { preset: 'last30' } },
        ],
      },
      {
        id: 'active',
        kind: 'timeseries',
        title: 'screen.retention.active',
        span: 7,
        metrics: ['user_events.active_clients', 'user_events.renting_clients'],
        chart: 'line',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'week',
        autoGranularity: true,
      },
      {
        id: 'buckets',
        kind: 'distribution',
        title: 'screen.retention.buckets',
        span: 5,
        dimension: 'clients.rents_bucket',
        metric: 'clients.count',
        chart: 'column',
        period: { preset: 'all' },
      },
      {
        id: 'monthly',
        kind: 'timeseries',
        title: 'screen.retention.monthly',
        span: 12,
        metrics: ['client_months.clients'],
        chart: 'bar',
        granularities: ['month'],
        defaultGranularity: 'month',
        splitBy: 'client_months.rents_bucket',
        stacked: true,
      },
      {
        id: 'flexible',
        kind: 'cohort',
        title: 'screen.retention.flexible',
        span: 12,
        metric: 'cohort_activity.members',
      },
      {
        id: 'cohorts',
        kind: 'cohort',
        title: 'screen.retention.cohorts',
        span: 12,
        metric: 'cohorts.rents',
      },
    ],
  },
  {
    id: 'clients.devices',
    title: 'nav.clients.devices',
    permission: sectionPermissions.clients,
    quickFilters: ['period', 'city', 'realOnly'],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          { metric: 'rents.web_share', compare: ['previous'] },
          { metric: 'logins.count', compare: ['previous'] },
          { metric: 'registrations.sber_count', compare: ['previous'] },
        ],
      },
      {
        id: 'channel',
        kind: 'distribution',
        title: 'screen.devices.channel',
        span: 4,
        dimension: 'rents.channel',
        metric: 'rents.count',
        chart: 'donut',
      },
      {
        id: 'os',
        kind: 'distribution',
        title: 'screen.devices.os',
        span: 4,
        dimension: 'logins.os_family',
        metric: 'logins.count',
        chart: 'donut',
      },
      {
        id: 'apps',
        kind: 'distribution',
        title: 'screen.devices.apps',
        span: 4,
        dimension: 'logins.channel',
        metric: 'logins.count',
        chart: 'donut',
      },
      {
        id: 'webTrend',
        kind: 'timeseries',
        title: 'screen.devices.webTrend',
        span: 12,
        metrics: ['rents.web_share'],
        chart: 'line',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'week',
      },
    ],
  },
  {
    id: 'clients.cards',
    title: 'nav.clients.cards',
    permission: sectionPermissions.clients,
    quickFilters: ['period', 'realOnly'],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          { metric: 'card_registrations.count', compare: ['previous'] },
          { metric: 'card_registrations.success_count', compare: ['previous'], spark: true },
          { metric: 'card_registrations.success_share', compare: ['previous'] },
        ],
      },
      {
        id: 'trend',
        kind: 'timeseries',
        title: 'screen.cards.trend',
        span: 12,
        metrics: ['card_registrations.count', 'card_registrations.success_count'],
        chart: 'bar',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'day',
      },
      {
        id: 'brands',
        kind: 'distribution',
        title: 'screen.cards.brands',
        span: 6,
        dimension: 'card_registrations.card_brand',
        metric: 'card_registrations.success_count',
        chart: 'donut',
      },
      {
        id: 'banks',
        kind: 'distribution',
        title: 'screen.cards.banks',
        span: 6,
        dimension: 'card_registrations.card_bank',
        metric: 'card_registrations.success_count',
        chart: 'bar',
      },
    ],
  },
  {
    id: 'payments.overview',
    title: 'nav.payments.overview',
    permission: sectionPermissions.payments,
    quickFilters: ['period', 'counterparty', 'group', 'realOnly'],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          { metric: 'payments.turnover', compare: ['previous'], spark: true },
          { metric: 'payments.payments_count', compare: ['previous'] },
          { metric: 'payments.avg_payment', compare: ['previous'] },
          { metric: 'payments.refunds_amount', compare: ['previous'] },
          { metric: 'shop_orders.unpaid_count', compare: ['previous'] },
          { metric: 'shop_orders.conversion', compare: ['previous'] },
          { metric: 'payments.active_counterparties', compare: ['previous'] },
          { metric: 'payments.commission_agent', compare: ['previous'] },
        ],
      },
      {
        id: 'trend',
        kind: 'timeseries',
        title: 'screen.payments.trend',
        span: 12,
        metrics: ['payments.turnover', 'payments.payments_count'],
        chart: 'barline',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'day',
      },
      {
        id: 'methods',
        kind: 'distribution',
        title: 'screen.payments.methods',
        span: 6,
        dimension: 'payments.method_type',
        metric: 'payments.turnover',
        chart: 'donut',
      },
      {
        id: 'gateways',
        kind: 'distribution',
        title: 'screen.payments.gateways',
        span: 6,
        dimension: 'payments.gateway',
        metric: 'payments.turnover',
        chart: 'bar',
      },
    ],
  },
  {
    id: 'payments.counterparties',
    title: 'nav.payments.counterparties',
    permission: sectionPermissions.payments,
    quickFilters: ['period', 'counterparty', 'group', 'realOnly'],
    defaultPeriod: { preset: 'today' },
    widgets: [
      {
        id: 'ranking',
        kind: 'ranking',
        title: 'screen.counterparties.ranking',
        span: 12,
        objectType: 'counterparty',
        metrics: [
          'payments.turnover',
          'payments.payments_count',
          'payments.refunds_amount',
          'payments.commission_agent',
        ],
        limit: 30,
        periods: ['today', 'yesterday', 'last7', 'last30', 'thisMonth'],
      },
      {
        id: 'list',
        kind: 'objects',
        title: 'screen.counterparties.list',
        span: 12,
        usePeriod: true,
        query: {
          objectType: 'counterparty',
          attributes: [
            'counterparties.shops_total',
            'counterparties.first_payment_at',
            'counterparties.last_payment_at',
          ],
          metrics: [
            'payments.payments_count',
            'payments.turnover',
            'shop_orders.unpaid_count',
            'payments.refunds_amount',
          ],
          order: [{ member: 'payments.turnover', direction: 'desc' }],
          limit: 1000,
          offset: 0,
        },
      },
      {
        id: 'compare',
        kind: 'compare',
        title: 'screen.counterparties.compare',
        span: 12,
        objectType: 'counterparty',
        metric: 'payments.turnover',
        granularity: 'week',
      },
    ],
  },
  {
    id: 'payments.methods',
    title: 'nav.payments.methods',
    permission: sectionPermissions.payments,
    quickFilters: ['period', 'counterparty', 'group', 'realOnly'],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'methods',
        kind: 'distribution',
        title: 'screen.payments.methods',
        span: 6,
        dimension: 'payments.method_type',
        metric: 'payments.payments_count',
        chart: 'donut',
      },
      {
        id: 'brands',
        kind: 'distribution',
        title: 'screen.payments.brands',
        span: 6,
        dimension: 'payments.card_brand',
        metric: 'payments.turnover',
        chart: 'donut',
      },
      {
        id: 'byMethod',
        kind: 'timeseries',
        title: 'screen.payments.byMethod',
        span: 12,
        metrics: ['payments.turnover'],
        chart: 'area',
        granularities: ['day', 'week', 'month'],
        defaultGranularity: 'week',
        splitBy: 'payments.method_type',
        stacked: true,
      },
      {
        id: 'banks',
        kind: 'distribution',
        title: 'screen.payments.banks',
        span: 12,
        dimension: 'payments.card_bank',
        metric: 'payments.payments_count',
        chart: 'bar',
      },
    ],
  },
  {
    id: 'payments.receipts',
    title: 'nav.payments.receipts',
    permission: sectionPermissions.payments,
    quickFilters: ['period', 'counterparty', 'group', 'realOnly'],
    defaultPeriod: { preset: 'last30' },
    widgets: [
      {
        id: 'kpis',
        kind: 'kpis',
        title: 'screen.kpis',
        span: 12,
        items: [
          { metric: 'payments.receipts_failed', compare: ['previous'] },
          { metric: 'payments.receipts_missing', compare: ['previous'] },
          { metric: 'payments.report_errors', compare: ['previous'] },
        ],
      },
      {
        id: 'trend',
        kind: 'timeseries',
        title: 'screen.receipts.trend',
        span: 12,
        metrics: ['payments.receipts_failed', 'payments.receipts_missing', 'payments.report_errors'],
        chart: 'bar',
        granularities: ['day', 'week'],
        defaultGranularity: 'day',
      },
      {
        id: 'failed',
        kind: 'records',
        title: 'screen.receipts.failed',
        span: 6,
        metric: 'payments.receipts_failed',
      },
      {
        id: 'missing',
        kind: 'records',
        title: 'screen.receipts.missing',
        span: 6,
        metric: 'payments.receipts_missing',
      },
    ],
  },
];

export function screenById(id: string): ScreenDef | undefined {
  return screens.find((s) => s.id === id);
}
